// What this surface is allowed to put on somebody else's phone, which is
// nothing that is not strictly necessary — and the mechanism that makes that a
// fact rather than an intention.
//
// THE RULING OF 2026-08-29, IN ONE SENTENCE: set no non-essential cookies or
// device storage at all. Everything in this file exists to make that ruling
// enforceable by a check rather than remembered by a person.
//
// WHY IT IS WORTH A MODULE. If the only storage is strictly necessary to deliver
// the thing the attendee asked for, then under ePrivacy Article 5(3) — and PECR
// regulation 6 in the UK — no *consent* is required for it, and the banner
// collapses from a consent gate into a short honest notice. That is dramatically
// cheaper to build, cheaper to be correct about, and cannot rot. The whole saving
// evaporates the first time somebody adds an analytics snippet, which is why the
// rule is written as a closed list with a refusal on the end rather than as a
// paragraph in a report.
//
// WHAT "STRICTLY NECESSARY" MEANS HERE, tested against the standard rather than
// asserted: the storage must be needed to provide a service the *user* has
// explicitly requested. The user has requested "register me at this stand". The
// ticket that proves they verified their mailbox is needed to complete that
// registration and is useless for anything else, expires in half an hour, and is
// gone when they close the tab. It is as clear a case as the exemption has.
//
// AND WHAT IS NOT ON THE LIST, said out loud because each of these is something
// a reasonable person would add without thinking:
//
//   No analytics cookies, and no analytics keys in this list. First-party pulse
//   counts which step of the page was reached — that is a POST to our own
//   origin, it writes nothing here, and a Global Privacy Control signal means
//   the POST is not made. Adding a key so the count could remember a visitor
//   would put a non-essential row on this list and turn the notice into a gate.
//
//   No remembering who somebody is between visits. A returning attendee retypes
//   their address and gets a new code. That is a worse experience and it is the
//   one that needs no consent, no banner and no withdrawal route.
//
//   No third-party embeds. No fonts from a CDN, no maps, no video players. Each
//   of those is an IP address handed to somebody else, which under the US state
//   laws is arguably a "share" and under GDPR is a transfer to name in a notice.
//
//   No CAPTCHA. The ruling is separate and the reasoning is in
//   xp/WEB-REPORT.md, but the storage consequence belongs here: reCAPTCHA sets
//   third-party cookies, which would put a non-essential category in the banner,
//   which makes the banner a consent gate again — and a control the visitor can
//   decline is not an anti-abuse control.

/**
 * Where a value can be put on somebody's device.
 *
 * The four the platform offers, named so the rule below can be about all of
 * them rather than about cookies. A policy that says "no cookies" and is silent
 * on localStorage is the policy every consent banner on the internet has, and it
 * is why they are all wrong: ePrivacy Article 5(3) is about "storing information,
 * or gaining access to information stored, in the terminal equipment", which is
 * technology-neutral and covers all four.
 */
export type StorageMedium = "cookie" | "local_storage" | "session_storage" | "indexed_db";

export const STORAGE_MEDIA: readonly StorageMedium[] = [
  "cookie",
  "local_storage",
  "session_storage",
  "indexed_db",
];

/**
 * A thing this surface is permitted to store, and the argument for it.
 *
 * `why` is not documentation. It is the sentence that goes in the cookie notice
 * — see notices.ts, which renders this list — so an entry with a weak `why` is a
 * weak sentence in a legal document rather than a weak comment.
 */
export type PermittedStorage = {
  readonly key: string;
  readonly medium: StorageMedium;
  /** What it is for, in words an attendee could read. */
  readonly why: string;
  /** When it goes, in words an attendee could read. */
  readonly lifetime: string;
};

/**
 * THE COMPLETE LIST, AND IT IS EMPTY.
 *
 * The confirmation that the emailed code was spent lives in React state
 * (`stage.proof`). Nothing calls `remember`. A list that named
 * `xp.registration.ticket` in sessionStorage while the page never wrote it was
 * the cookie notice describing a key a person cannot find in their browser —
 * the Infinity Marketing "purpose pending investigation" failure in the other
 * direction, and the reason docs/legal/inventory.md exists.
 *
 * Empty is therefore the honest closed list, not a gap waiting to be filled.
 * Adding a key is a legal change: it appears in the generated notice the same
 * moment it is writable, which is the coupling this file exists to enforce.
 *
 * If a later change does persist the ticket, sessionStorage is the medium, not
 * a cookie and not localStorage. A cookie travels on every request to the
 * origin. localStorage outlives the tab and becomes a persistent identifier.
 * sessionStorage dies with the tab, which is the lifetime of the registration.
 */
export const PERMITTED_STORAGE: readonly PermittedStorage[] = [];

/**
 * Whether a proposed piece of storage is on the list.
 *
 * The list is closed, and this is the function that makes it closed. Everything
 * in this package that writes to the device goes through `remember` below, and
 * `remember` refuses anything this returns false for — so adding storage means
 * adding an entry here, which means adding a sentence to the cookie notice,
 * which is exactly the coupling that keeps the notice true.
 */
export function isPermitted(key: string, medium: StorageMedium): boolean {
  return PERMITTED_STORAGE.some((entry) => entry.key === key && entry.medium === medium);
}

/**
 * Whether this surface needs consent before storing anything.
 *
 * Computed from NON_ESSENTIAL rather than declared, which is the point. Today
 * it is false because that list is empty — and so is PERMITTED_STORAGE, because
 * this page writes nothing. The day somebody adds an analytics key, NON_ESSENTIAL
 * gains a row, this becomes true, and notices.ts turns the notice into a gate
 * rather than the notice quietly continuing to say "we set nothing you need to
 * agree to" while an analytics cookie sits underneath it.
 *
 * `NON_ESSENTIAL` is empty and is a separate constant rather than a filter over
 * PERMITTED_STORAGE, because the two lists are different kinds of thing: one is
 * a permission and the other is a declaration of what needs asking about. Merging
 * them would mean an entry could be added to storage without anybody deciding
 * which kind it was.
 */
export const NON_ESSENTIAL: readonly PermittedStorage[] = [];

export function consentIsRequiredBeforeStoring(): boolean {
  return NON_ESSENTIAL.length > 0;
}

/**
 * The one place this package writes to the device.
 *
 * A wrapper rather than a convention, because a convention is a comment and this
 * is a refusal. `check.mjs` reads the source of this package and fails if
 * `sessionStorage`, `localStorage`, `document.cookie` or `indexedDB` appear
 * anywhere outside this file — which is the half of the rule that a runtime
 * guard cannot enforce, since code that calls the platform directly never
 * reaches this function.
 *
 * @throws if the key is not on the list. Loudly, in every environment, rather
 *   than falling back to not storing: a silent no-op would mean the feature
 *   somebody was adding appears broken, they debug it for an hour, and then they
 *   remove the wrapper. A throw sends them to this file, which is where the
 *   decision they need to make is written down.
 */
export function remember(store: Pick<Storage, "setItem">, key: string, value: string): void {
  if (!isPermitted(key, "session_storage")) {
    throw new Error(
      `xp/web tried to store ${JSON.stringify(key)} on the attendee's device, and it is not on the list ` +
        "in src/privacy/storage.ts.\n\n" +
        "  That list is closed on purpose. Everything on it is strictly necessary to deliver the " +
        "registration the attendee asked for, which is what lets this surface show a notice instead of a " +
        "consent gate. Adding something that is not strictly necessary is a decision with legal " +
        "consequences, not a line of code: it puts a non-essential category in the banner, and a banner " +
        "with a category in it must be refusable as easily as it is acceptable.\n\n" +
        "  If the new item really is strictly necessary, add it to PERMITTED_STORAGE with the sentence " +
        "an attendee will read about it. If it is not, it needs the owner's ruling first.",
    );
  }
  store.setItem(key, value);
}

/**
 * Whether the browser is asking not to be sold to or shared with.
 *
 * GLOBAL PRIVACY CONTROL, WHICH IS CODE RATHER THAN POLICY. `navigator.globalPrivacyControl`
 * is a boolean the browser sets, and Colorado's rules and the CPPA's enforcement
 * treat it as a valid opt-out request that must be honoured — it is not advisory.
 *
 * WHAT IT CHANGES ON THIS SURFACE TODAY: nothing, and that is the answer rather
 * than a gap. Nothing here is sold and nothing is shared for cross-context
 * behavioural advertising, so the signal is already honoured by construction.
 * The function exists anyway for two reasons. It lets the page *say* the signal
 * was seen and honoured, which is worth more to a person who set it than silence
 * is. And it is the seam: if a marketing consent is ever added that amounts to a
 * "share", this is the input that must switch it off, and finding this function
 * already here is what stops that being written from scratch and wrongly.
 *
 * Takes the navigator rather than reading the global, so the check can drive
 * both answers without a DOM.
 */
export function globalPrivacyControlIsSet(nav: { globalPrivacyControl?: unknown } | undefined): boolean {
  // Strict `=== true`, not truthiness. The specification says the property is a
  // boolean and some extensions have historically set the string "1"; treating
  // that as set would be generous, and being generous in the direction of
  // honouring an opt-out is right — but treating an *absent* property's
  // `undefined` as anything is how a bug becomes a claim that every visitor
  // opted out, which would make the claim meaningless when it mattered.
  return nav?.globalPrivacyControl === true;
}
